import { LicenceId } from '@ps/platform-message-framework/common/LicenceId';
import { ResolverTag } from '@ps/web-core-enums';
import type { TranslationTag } from '@ps/web-core-intl';
import {
  StarsButton as Button,
  Toggle,
  Translation,
  toLocaleDateString
} from '@ps/web-shared-components';
import { createForm } from '@ps/web-shared-ui';
import classNames from 'clsx';
import type { FormApi } from 'final-form';
import React from 'react';
import type { SyntheticEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import CurrencyAmount from '@swf/components/CurrencyAmount/CurrencyAmount';
import ResolverLink from '@swf/components/ResolverLink/ResolverLink';
import type { CardViewProps } from '@swf/components/StarsMPNotification/config/NotificationTypes';
import { geti18NValueConfig } from '@swf/components/StarsMPNotification/utils';
import LicensedTranslation from '@swf/components/Translation/LicensedTranslation';
import freeSpinsIcon from '@swf/core/assets/casino/free-spins.png';
import type { Dispatch } from '@swf/core/store';
import { select } from '@swf/core/store';
import type { FreeSpinsNotification } from '@swf/core/types/Notification';
import { NotificationContainer } from '@swf/notification';

import sharedStyles from '../../../styles/shared.scss';
import Buttons from '../../Buttons/Buttons';
// TODO refactor to use composition or shared.scss
// eslint-disable-next-line no-restricted-imports
import styles from '../FreeSpins.scss';

import {
  TXTCLI_1_Free_Spin,
  TXTCLI_Accept,
  TXTCLI_As_soon_as_you_complete_all_yo_ELL,
  TXTCLI_As_soon_as_you_complete_all_yo_ELL_1,
  TXTCLI_As_soon_as_you_complete_all_yo_ELL_2,
  TXTCLI_As_soon_as_you_complete_all_yo_ELL_3,
  TXTCLI_As_soon_as_you_complete_all_yo_ELL_4,
  TXTCLI_As_soon_as_you_complete_all_yo_ELL_5,
  TXTCLI_As_soon_as_you_complete_all_yo_ELL_6,
  TXTCLI_As_soon_as_you_complete_all_yo_ELL_7,
  TXTCLI_As_soon_as_you_complete_all_yo_ELL_9,
  TXTCLI_As_soon_as_you_have_played_all_ELL,
  TXTCLI_As_soon_as_you_have_played_all_ELL_1,
  TXTCLI_As_soon_as_you_have_played_all_ELL_2,
  TXTCLI_By_accepting_this_offerX_you_a_ELL,
  TXTCLI_CongratulationsX_you_have_been_ELL,
  TXTCLI_Decline,
  TXTCLI_Good_LuckX,
  TXTCLI_LoadingXX,
  TXTCLI_More_information,
  TXTCLI_N0_Free_Spins,
  TXTCLI_RememberX_you_must_use_your_Fr_ELL,
  TXTCLI_ThereafterX_you_would_need_to__ELL,
  TXTCLI_You_can_use_your_Free_Spins_to_ELL,
  TXTMOB_Something_went_wrongX_Please_t_ELL,
  TXTWCL_As_soon_as_you_complete_all_yo_ELL,
  TXTWCL_As_soon_as_you_complete_all_yo_ELL_3
} from './Issued.messages.json';

enum Accept {
  YES = 'yes',
  NO = 'no'
}

interface FormValues {
  freeSpinAccept: Accept;
}

const freeSpinMessages: Record<
  | 'freeSpinBonusTermsCash'
  | 'freeSpinBonusTermsBonusCap'
  | 'freeSpinBonusTermsBonus'
  | 'freeSpinBonusTermsCashCap',
  { [key in LicenceId]?: TranslationTag } & { Default: TranslationTag }
> = {
  freeSpinBonusTermsCash: {
    [LicenceId.PORTUGAL]: TXTCLI_As_soon_as_you_have_played_all_ELL_2,
    Default: TXTCLI_As_soon_as_you_complete_all_yo_ELL_7
  },
  freeSpinBonusTermsBonusCap: {
    [LicenceId.BELGIUM]: TXTCLI_As_soon_as_you_complete_all_yo_ELL_5,
    [LicenceId.DENMARK]: TXTCLI_As_soon_as_you_complete_all_yo_ELL_4,
    [LicenceId.ITALY]: TXTCLI_As_soon_as_you_have_played_all_ELL_1,
    [LicenceId.PORTUGAL]: TXTCLI_As_soon_as_you_have_played_all_ELL_1,
    [LicenceId.SPAIN]: TXTWCL_As_soon_as_you_complete_all_yo_ELL_3,
    Default: TXTCLI_As_soon_as_you_complete_all_yo_ELL_3
  },
  freeSpinBonusTermsBonus: {
    [LicenceId.BELGIUM]: TXTCLI_As_soon_as_you_complete_all_yo_ELL_2,
    [LicenceId.DENMARK]: TXTCLI_As_soon_as_you_complete_all_yo_ELL_1,
    [LicenceId.ITALY]: TXTCLI_As_soon_as_you_have_played_all_ELL,
    [LicenceId.PORTUGAL]: TXTCLI_As_soon_as_you_have_played_all_ELL,
    [LicenceId.SPAIN]: TXTWCL_As_soon_as_you_complete_all_yo_ELL,
    Default: TXTCLI_As_soon_as_you_complete_all_yo_ELL
  },
  freeSpinBonusTermsCashCap: {
    [LicenceId.SPAIN]: TXTCLI_As_soon_as_you_complete_all_yo_ELL_9,
    Default: TXTCLI_As_soon_as_you_complete_all_yo_ELL_6
  }
};

const getFreeSpinMessage = (
  tag: keyof typeof freeSpinMessages,
  license: LicenceId
): TranslationTag => freeSpinMessages[tag][license] ?? freeSpinMessages[tag].Default;

const Form = createForm<FormValues>();

const Issued: React.FC<CardViewProps & { cardData: FreeSpinsNotification }> = ({
  cardData: {
    attributes,
    clearBets,
    currency,
    description,
    expiredDate,
    hasError,
    notificationId,
    redemptionLimit = 0,
    rewardAssetType,
    rollId,
    status,
    submitted,
    timeZone,
    type
  },
  viewConfig: { title, actions }
}) => {
  const license = useSelector(select.account.getLicense);
  const language = useSelector(select.locale.get);
  const { modal, notification } = useDispatch<Dispatch>();

  const handleSubmit = (form: FormApi<FormValues>) => {
    const {
      values: { freeSpinAccept }
    } = form.getState();
    notification.acceptBonus({
      rollId,
      status,
      notificationId,
      acceptBonus: freeSpinAccept === Accept.YES
    });
    modal.close('/mpnotifications');
  };

  const viewBonuses = (e: SyntheticEvent) => {
    e.preventDefault();
    modal.replace([{ path: `/account/my-stars/bonus/free-spin-bonuses/${rollId}` }]);
  };

  return (
    <NotificationContainer
      title={<Translation {...title.tag} values={geti18NValueConfig(title.values, attributes)} />}
      bodyClass={classNames(sharedStyles.containerBody, sharedStyles.alignLeft)}
    >
      <img className={styles.freeSpins} src={freeSpinsIcon} alt="Free Spins Icon" />
      {clearBets === 1 && <Translation tagName="h3" {...TXTCLI_1_Free_Spin} />}
      {(clearBets as number) > 1 && (
        <Translation tagName="h3" {...TXTCLI_N0_Free_Spins} values={{ 0: clearBets }} />
      )}
      <Form
        onSubmit={() => undefined}
        initialValues={{ freeSpinAccept: Accept.YES }}
        render={({ form }) => (
          <>
            <Translation
              {...TXTCLI_CongratulationsX_you_have_been_ELL}
              values={{ 0: clearBets, 1: description }}
            />{' '}
            <Translation {...TXTCLI_You_can_use_your_Free_Spins_to_ELL} />{' '}
            {expiredDate && (
              <Translation
                {...TXTCLI_RememberX_you_must_use_your_Fr_ELL}
                values={{ 0: `${toLocaleDateString(language, expiredDate)} ${timeZone}` }}
              />
            )}{' '}
            <Translation
              {...getFreeSpinMessage(
                rewardAssetType === 10
                  ? (redemptionLimit as number) > 0
                    ? 'freeSpinBonusTermsBonusCap'
                    : 'freeSpinBonusTermsBonus'
                  : (redemptionLimit as number) > 0
                    ? 'freeSpinBonusTermsCashCap'
                    : 'freeSpinBonusTermsCash',
                license
              )}
              values={{
                0: <CurrencyAmount value={redemptionLimit} currency={currency} />
              }}
            />{' '}
            {rewardAssetType === 10 && (
              <LicensedTranslation
                include={[LicenceId.ITALY, LicenceId.PORTUGAL]}
                {...TXTCLI_ThereafterX_you_would_need_to__ELL}
              />
            )}
            <LicensedTranslation exclude={[LicenceId.ITALY]} {...TXTCLI_Good_LuckX} />
            <LicensedTranslation
              include={[LicenceId.UK]}
              tagName="p"
              {...TXTCLI_By_accepting_this_offerX_you_a_ELL}
              values={{
                a: (msg: React.ReactNode) => (
                  <ResolverLink
                    resolverTag={
                      rewardAssetType === 10
                        ? (redemptionLimit as number) > 0
                          ? ResolverTag.FreeSpinBonusTermsBonusCap
                          : ResolverTag.FreeSpinBonusTermsBonus
                        : (redemptionLimit as number) > 0
                          ? ResolverTag.FreeSpinBonusTermsCashCap
                          : ResolverTag.FreeSpinBonusTermsCash
                    }
                  >
                    {msg}
                  </ResolverLink>
                )
              }}
            />
            <Toggle
              id="fs-accept-yes"
              name="freeSpinAccept"
              key={Accept.YES}
              label={
                <span>
                  <Translation {...TXTCLI_Accept} />
                </span>
              }
              type="radio"
              value={Accept.YES}
            />
            <Toggle
              id="fs-accept-no"
              name="freeSpinAccept"
              key={Accept.NO}
              label={
                <span>
                  <Translation {...TXTCLI_Decline} />
                </span>
              }
              type="radio"
              value={Accept.NO}
            />
            <Button
              variant="text"
              onClick={viewBonuses}
              className={styles.moreInfoBtn}
              translation={TXTCLI_More_information}
            />
            {hasError && (
              <div className={sharedStyles.errorMessage}>
                <Translation {...TXTMOB_Something_went_wrongX_Please_t_ELL} />
              </div>
            )}
            {submitted ? (
              <Button className={sharedStyles.submitBtn} disabled translation={TXTCLI_LoadingXX} />
            ) : (
              <Buttons
                actionList={actions}
                notificationType={type}
                attributes={attributes}
                notificationId={notificationId}
                onClickPrimaryAction={() => handleSubmit(form)}
              />
            )}
          </>
        )}
      />
    </NotificationContainer>
  );
};

export default Issued;
